/**
 * LSA Equipment Section Component
 * Handles Life-Saving Appliances selection with mandatory auto-triggers
 */

import React from 'react';
import { Info } from 'lucide-react';
import { Switch } from '../../../../../catalyst/checkbox';
import WizardSection from '../../WizardSection';

const LSAEquipmentSection = ({ 
  draftConfig, 
  updateConfig, 
  lsaEquipmentOptions,
  immersionSuitsReason,
  tpaReason,
  tpaQuantity,
  lineThrowingReason,
  immersionSuitsMandatory,
  tpaMandatory,
  lineThrowingMandatory
}) => {
  return (
    <WizardSection.Card data-testid="lsa-equipment-section">
      <WizardSection.Header>Life-Saving Appliances (LSA)</WizardSection.Header>
      
      {/* Mandatory Equipment Info Callouts */}
      {immersionSuitsReason && (
        <WizardSection.Callout variant="info">
          <div className="flex items-start gap-2">
            <Info className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm"><strong>Immersion Suits:</strong> {immersionSuitsReason}</p>
          </div>
        </WizardSection.Callout>
      )}
      
      {tpaReason && (
        <WizardSection.Callout variant="info">
          <div className="flex items-start gap-2">
            <Info className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm"><strong>Thermal Protective Aids ({tpaQuantity}):</strong> {tpaReason}</p>
          </div>
        </WizardSection.Callout>
      )}
      
      {lineThrowingReason && (
        <WizardSection.Callout variant="info">
          <div className="flex items-start gap-2">
            <Info className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm"><strong>Line Throwing Apparatus (2 units):</strong> {lineThrowingReason}</p>
          </div>
        </WizardSection.Callout>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
        {lsaEquipmentOptions.map(equipment => {
          // Check if this equipment is mandatory based on vessel config
          const isMandatory = (
            (equipment.value === 'immersion_suits' && immersionSuitsMandatory) ||
            (equipment.value === 'thermal_protective_aids' && tpaMandatory) ||
            (equipment.value === 'line_throwing_apparatus' && lineThrowingMandatory)
          );
          
          // For mandatory items, show as checked even if not yet in array (handles timing issues)
          const isChecked = isMandatory || (draftConfig.lsaEquipment?.includes(equipment.value) || false);
          
          // Get the mandatory reason for display
          let mandatoryReason = '';
          if (equipment.value === 'immersion_suits' && immersionSuitsMandatory) {
            mandatoryReason = immersionSuitsReason;
          } else if (equipment.value === 'thermal_protective_aids' && tpaMandatory) {
            mandatoryReason = `${tpaQuantity} - ${tpaReason}`;
          } else if (equipment.value === 'line_throwing_apparatus' && lineThrowingMandatory) {
            mandatoryReason = lineThrowingReason;
          }
          
          return (
            <label 
              key={equipment.value} 
              className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                isMandatory 
                  ? 'border-blue-300 bg-blue-50' 
                  : 'border-gray-200 hover:bg-gray-50'
              }`}
            >
              <Switch
                checked={isChecked}
                disabled={isMandatory}
                onChange={(checked) => {
                  if (isMandatory) return; // Don't allow unchecking mandatory items
                  const currentEq = draftConfig.lsaEquipment || [];
                  const newEq = checked 
                    ? [...currentEq, equipment.value]
                    : currentEq.filter(e => e !== equipment.value);
                  
                  // Sync legacy fields with lsaEquipment array
                  const updates = { lsaEquipment: Array.from(new Set(newEq)) };
                  
                  // Map to legacy field names for backwards compatibility
                  if (equipment.value === 'immersion_suits') {
                    updates.hasImmersionSuits = checked;
                  } else if (equipment.value === 'line_throwing_apparatus') {
                    updates.hasLineThrowing = checked;
                  } else if (equipment.value === 'thermal_protective_aids') {
                    // Only update if user explicitly toggles TPA (not when auto-mandatory)
                    if (!tpaMandatory) {
                      updates.hasThermalProtectiveAids = checked;
                    }
                  }
                  
                  updateConfig(updates);
                }}
                className="mt-0.5"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-900">{equipment.label}</span>
                  {isMandatory && (
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                      Mandatory
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-600 mt-1">{equipment.help}</p>
                <p className="text-xs text-gray-500 mt-1 font-medium">{equipment.reference}</p>
              </div>
            </label>
          );
        })}
      </div>
    </WizardSection.Card>
  );
};

export default LSAEquipmentSection;